import type { NodeData } from '@spotar/runtime';
import { runDelayImplementation } from './node-implementations/delay-implementation';
import { runPlayAnimationImplementation } from './node-implementations/play-animation-implementation';
import { runPlayVideoImplementation } from './node-implementations/play-video-implementation';
import { runPlayAudioImplementation } from './node-implementations/play-audio-implementation';
import { runPrefetchAssetImplementation } from './node-implementations/prefetch-asset-implementation';
import { runTranslateTextImplementation } from './node-implementations/translate-text-implementation';
import { runTextToSpeechImplementation } from './node-implementations/text-to-speech-implementation';
import {
  runStopAnimationImplementation,
  runStopAllAnimationsOfObjectImplementation,
  runStopAllAnimationsImplementation,
} from './node-implementations/stop-animation-implementation';
import {
  runStopVideoImplementation,
  runStopAllVideosImplementation,
} from './node-implementations/stop-video-implementation';
import {
  runStopAudioImplementation,
  runStopAllAudioImplementation,
} from './node-implementations/stop-audio-implementation';
import {
  runStopPlaybackImplementation,
  runStopAllPlaybackImplementation,
} from './node-implementations/stop-playback-implementation';
import {
  runPrintImplementation,
  runPrintVarImplementation,
} from './node-implementations/print-implementation';
import { runLogNotificationImplementation } from './node-implementations/log-notification-implementation';
import { runSetVisibleImplementation } from './node-implementations/set-visible-implementation';
import { runFadeVisibleImplementation } from './node-implementations/fade-visible-implementation';
import { runMoveToImplementation } from './node-implementations/move-to-implementation';
import { runRotateToImplementation } from './node-implementations/rotate-to-implementation';
import { runScaleToImplementation } from './node-implementations/scale-to-implementation';
import { runCharacterMoveToImplementation } from './node-implementations/character-move-to-implementation';
import { runSetSkySphereImplementation } from './node-implementations/set-sky-sphere-implementation';
import { runAudioPlayerLoadTrackImplementation } from './node-implementations/audioplayer-load-track-implementation';
import { runAudioPlayerPlayImplementation } from './node-implementations/audioplayer-play-implementation';
import { runAudioPlayerPauseImplementation } from './node-implementations/audioplayer-pause-implementation';
import { runAudioPlayerShowUiImplementation } from './node-implementations/audioplayer-show-ui-implementation';
import { runTraySpawnChoiceImplementation } from './node-implementations/tray-spawn-choice-implementation';
import { runTrayHighlightImplementation } from './node-implementations/tray-highlight-implementation';
import {
  runTrayDisableInputImplementation,
  runTrayEnableInputImplementation,
} from './node-implementations/tray-input-state-implementation';
import { runOnTrayChoiceChangedImplementation } from './node-implementations/on-tray-choice-changed-implementation';
import { runOnLoadFinishedImplementation } from './node-implementations/on-load-finished-implementation';
import { runOnStartImplementation } from './node-implementations/on-start-implementation';
import { runOnClickImplementation } from './node-implementations/on-click-implementation';
import { runOnHitboxClickImplementation } from './node-implementations/on-hitbox-click-implementation';
import {
  runOnPlayerTrackFinishedImplementation,
  runOnPlayerTrackFinishedAndAudioActiveImplementation,
  runOnPlayerPlayImplementation,
  runOnPlayerPlayAndAudioActiveImplementation,
  runOnPlayerPausedImplementation,
  runOnPlayerPausedAndAudioActiveImplementation,
} from './node-implementations/on-player-events-implementation';
import { getHitboxValueImplementation } from './node-implementations/get-hitbox-implementation';

/**
 * Assembles concrete node implementations for RuntimeEngine.init.
 * Cast to NodeData: impls use narrow data shapes for authoring, while the
 * runtime always passes Record<string, unknown> (parameter contravariance).
 */
export function collectNodeImplementations(): NodeData {
  return {
    valueImplementations: {
      getHitbox: getHitboxValueImplementation,
    },
    runImplementations: {
      delay: runDelayImplementation,
      playAnimation: runPlayAnimationImplementation,
      playVideo: runPlayVideoImplementation,
      playAudio: runPlayAudioImplementation,
      prefetchAsset: runPrefetchAssetImplementation,
      translateText: runTranslateTextImplementation,
      textToSpeech: runTextToSpeechImplementation,
      stopAnimation: runStopAnimationImplementation,
      stopAllAnimationsOfObject: runStopAllAnimationsOfObjectImplementation,
      stopAllAnimations: runStopAllAnimationsImplementation,
      stopVideo: runStopVideoImplementation,
      stopAllVideos: runStopAllVideosImplementation,
      stopAudio: runStopAudioImplementation,
      stopAllAudio: runStopAllAudioImplementation,
      stopPlayback: runStopPlaybackImplementation,
      stopAllPlayback: runStopAllPlaybackImplementation,
      setVisible: runSetVisibleImplementation,
      fadeVisible: runFadeVisibleImplementation,
      rotateTo: runRotateToImplementation,
      moveTo: runMoveToImplementation,
      scaleTo: runScaleToImplementation,
      characterMoveTo: runCharacterMoveToImplementation,
      setSkySphere: runSetSkySphereImplementation,
      playerShow: runAudioPlayerShowUiImplementation,
      playerLoadTrack: runAudioPlayerLoadTrackImplementation,
      playerPlay: runAudioPlayerPlayImplementation,
      playerPause: runAudioPlayerPauseImplementation,
      traySpawnChoice: runTraySpawnChoiceImplementation,
      trayHighlight: runTrayHighlightImplementation,
      trayDisableInput: runTrayDisableInputImplementation,
      trayEnableInput: runTrayEnableInputImplementation,
      print: runPrintImplementation,
      printVar: runPrintVarImplementation,
      logNotification: runLogNotificationImplementation,
    },
    eventImplementations: {
      onLoadFinished: runOnLoadFinishedImplementation,
      // Legacy graphs saved before the rename still use onLoad.
      onLoad: runOnLoadFinishedImplementation,
      onStart: runOnStartImplementation,
      onClick: runOnClickImplementation,
      onHitboxClick: runOnHitboxClickImplementation,
      onTrayChoiceChanged: runOnTrayChoiceChangedImplementation,
      onPlayerPlay: runOnPlayerPlayImplementation,
      onPlayerPlayAndAudioActive: runOnPlayerPlayAndAudioActiveImplementation,
      onPlayerTrackFinished: runOnPlayerTrackFinishedImplementation,
      onPlayerTrackFinishedAndAudioActive: runOnPlayerTrackFinishedAndAudioActiveImplementation,
      onPlayerPaused: runOnPlayerPausedImplementation,
      onPlayerPausedAndAudioActive: runOnPlayerPausedAndAudioActiveImplementation,
    },
  } as unknown as NodeData;
}
